import { Link } from 'react-router-dom';
import { Badge, Button, getStatusVariant } from '@/components/ui';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import type { EventSubscription } from '../../types';
import { SubscriptionFilterSummary } from './SubscriptionFilterSummary';

const DELIVERY_LABELS: Record<string, string> = {
  in_session: 'In Session',
  trigger: 'Trigger',
};

interface SubscriptionRowProps {
  subscription: EventSubscription;
  onToggle: (subscription: EventSubscription) => void;
  isPending: boolean;
}

export function SubscriptionRow({
  subscription: sub,
  onToggle,
  isPending,
}: SubscriptionRowProps) {
  const spaceId = useSpaceId();
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-foreground truncate">
            {sub.filter.eventType ?? '*'}
          </span>
          <Badge variant={sub.scope === 'dynamic' ? 'info' : 'gray'}>
            {sub.scope}
          </Badge>
          <Badge variant="info">
            {DELIVERY_LABELS[sub.delivery.mode] ?? sub.delivery.mode}
          </Badge>
          <Badge variant={getStatusVariant(sub.status)}>{sub.status}</Badge>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
          <span>
            Agent:{' '}
            <Link
              to={routes.console.spaceAgent(spaceId, sub.subscriberId)}
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {sub.subscriberName}
            </Link>
          </span>
          {sub.filter.source && <span>Source: {sub.filter.source}</span>}
          {sub.filter.subjectType && <span>Subject: {sub.filter.subjectType}</span>}
          {sub.filter.subjectId && <span>ID: {sub.filter.subjectId}</span>}
          {sub.delivery.priority === 'high' && (
            <span className="text-orange-600 dark:text-orange-400">High priority</span>
          )}
          {sub.delivery.mode === 'trigger' &&
            sub.delivery.conversationStrategy === 'per_event' && (
              <span>New conversation per event</span>
            )}
        </div>
        <SubscriptionFilterSummary filter={sub.filter} />
      </div>
      <div className="flex items-center gap-2 ml-4 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle(sub)}
          disabled={isPending}
        >
          {sub.status === 'active' ? 'Pause' : 'Resume'}
        </Button>
      </div>
    </div>
  );
}
